import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LanguageProvider } from "./contexts/LanguageContext";
import { useAnalytics } from "./hooks/useAnalytics";
import Index from "./pages/Index";
import About from "./pages/About";
import Services from "./pages/Services";
import Partners from "./pages/Partners";
import News from "./pages/News";
import NewsDetail from "./pages/NewsDetail";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminNews from "./pages/AdminNews";
import AdminNewsForm from "./pages/AdminNewsForm";
import AdminProducts from "./pages/AdminProducts";
import AdminGallery from "./pages/AdminGallery";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import Laboratory from '@/pages/Laboratory';
import Laboratories from './pages/Laboratories';
import Gallery from './pages/Gallery';
import { AdminLabProducts, AdminUsers } from './pages/admin';
import AdminLaboratories from './pages/admin/AdminLaboratories';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Composant pour tracker les analytics
const AnalyticsTracker = () => {
  useAnalytics();
  return null;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <Router>
            <AnalyticsTracker />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Index />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/news" element={<News />} />
              <Route path="/news/:id" element={<NewsDetail />} />
              <Route path="/laboratories" element={<Laboratories />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/laboratory/:id" element={<Laboratory />} />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route 
                path="/admin/dashboard" 
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/news" 
                element={
                  <AdminProtectedRoute>
                    <AdminNews />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/news/new" 
                element={
                  <AdminProtectedRoute>
                    <AdminNewsForm />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/news/edit/:id" 
                element={
                  <AdminProtectedRoute>
                    <AdminNewsForm />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/products" 
                element={
                  <AdminProtectedRoute>
                    <AdminProducts />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/gallery" 
                element={
                  <AdminProtectedRoute>
                    <AdminGallery />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/laboratories/:labId/products" 
                element={
                  <AdminProtectedRoute>
                    <AdminLabProducts />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/laboratories" 
                element={
                  <AdminProtectedRoute>
                    <AdminLaboratories />
                  </AdminProtectedRoute>
                } 
              />
              <Route 
                path="/admin/users" 
                element={
                  <AdminProtectedRoute>
                    <AdminUsers />
                  </AdminProtectedRoute>
                } 
              />

              {/* 404 Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
          <Toaster />
          <Sonner />
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;
